import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { AlertTriangle, ArrowLeft } from "lucide-react";
import { Button } from "./ui/button";

/**
 * ImpersonationBanner - Shows a red banner when admin is impersonating a user
 * 
 * Checks JWT token for isImpersonation === true
 * Provides "Zurück zu Admin" button to restore admin session
 */
export default function ImpersonationBanner() {
  const navigate = useNavigate();
  const [impersonationData, setImpersonationData] = useState(null);

  useEffect(() => {
    checkImpersonation();
  }, []);

  const checkImpersonation = () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const decoded = jwtDecode(token);
      
      // Check if this is an impersonation token
      if (decoded.isImpersonation === true) {
        setImpersonationData({
          userName: decoded.name || decoded.email || "Unbekannter User",
          userId: decoded.sub || decoded.user_id
        });
      }
    } catch (error) {
      console.error("Error decoding token:", error);
    }
  };

  const handleBackToAdmin = () => {
    try {
      // Get the backed up admin token and user data
      const adminToken = localStorage.getItem("adminTokenBackup");
      const adminUserData = localStorage.getItem("adminUserBackup");
      
      if (adminToken) {
        // Restore admin token as active token
        localStorage.setItem("token", adminToken);
        
        // Restore full admin user data (with permissions, language, etc.)
        if (adminUserData) {
          localStorage.setItem("user", adminUserData);
        } else {
          // Fallback: decode token for minimal data
          const adminDecoded = jwtDecode(adminToken);
          localStorage.setItem("user", JSON.stringify({
            id: adminDecoded.sub || adminDecoded.user_id,
            email: adminDecoded.email,
            name: adminDecoded.name,
            role: adminDecoded.role
          }));
        }
        
        // Remove backups
        localStorage.removeItem("adminTokenBackup");
        localStorage.removeItem("adminUserBackup");
        
        // Navigate to admin dashboard
        navigate("/app");
        
        // Force reload to refresh auth context
        window.location.reload();
      } else {
        // Fallback: just logout and redirect
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
      }
    } catch (error) {
      console.error("Error restoring admin session:", error);
      // Fallback to login
      navigate("/login");
    }
  };

  // Don't render if not impersonating
  if (!impersonationData) {
    return null;
  }

  return (
    <div 
      className="bg-red-600 text-white px-4 py-3 flex items-center justify-between shadow-md"
      data-testid="impersonation-banner"
    >
      <div className="flex items-center gap-3">
        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        <span className="font-medium">
          Du arbeitest aktuell als <strong>{impersonationData.userName}</strong>.
        </span>
      </div>
      
      <Button
        variant="outline"
        size="sm"
        onClick={handleBackToAdmin}
        className="bg-white text-red-600 hover:bg-red-50 border-white hover:border-red-100 flex items-center gap-2"
        data-testid="back-to-admin-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Zurück zu Admin
      </Button>
    </div>
  );
}
